import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Users, MessageSquare, Trophy, Heart, Send, Plus, X,
  ArrowLeft, Flame, Target, Award, Loader2, Eye, EyeOff, Trash2
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { OposaiLogo } from '@/components/TuPlazaIALogo';
import { motion, AnimatePresence } from 'framer-motion';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CommunityPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('feed');
  const [posts, setPosts] = useState([]);
  const [challenges, setChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewPostModal, setShowNewPostModal] = useState(false);
  const [showNewChallengeModal, setShowNewChallengeModal] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [newPost, setNewPost] = useState({
    content: '',
    post_type: 'question',
    is_anonymous: false,
    display_name: ''
  });
  const [newChallenge, setNewChallenge] = useState({
    title: '',
    description: '',
    target_questions: 100,
    end_date: '',
    is_public: true
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('session_token');
      const headers = { Authorization: `Bearer ${token}` };

      if (activeTab === 'feed' || activeTab === 'posts') {
        const res = await axios.get(`${API}/community/posts`, { headers });
        setPosts(res.data.posts || []);
      }
      
      if (activeTab === 'challenges') {
        const res = await axios.get(`${API}/community/challenges`, { headers });
        setChallenges(res.data.challenges || []);
      }
      
      if (activeTab === 'feed') {
        const supportRes = await axios.get(`${API}/community/support/feed`, { headers });
        setChallenges(supportRes.data.active_challenges || []);
      }
    } catch (error) {
      console.error('Error loading community data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePost = async (e) => {
    e.preventDefault();
    if (!newPost.content.trim()) {
      toast.error('Escribe algo para publicar');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('session_token');
      await axios.post(`${API}/community/posts`, newPost, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('¡Publicación creada! +15 XP');
      setShowNewPostModal(false);
      setNewPost({ content: '', post_type: 'question', is_anonymous: false, display_name: '' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear la publicación');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateChallenge = async (e) => {
    e.preventDefault();
    if (!newChallenge.title.trim() || !newChallenge.end_date) {
      toast.error('Completa todos los campos obligatorios');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('session_token');
      await axios.post(`${API}/community/challenges`, newChallenge, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('¡Reto creado!');
      setShowNewChallengeModal(false);
      setNewChallenge({ title: '', description: '', target_questions: 100, end_date: '', is_public: true });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear el reto');
    } finally {
      setSubmitting(false);
    }
  };

  const handleLikePost = async (postId) => {
    try {
      const token = localStorage.getItem('session_token');
      const res = await axios.post(`${API}/community/posts/${postId}/like`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPosts(posts.map(p => 
        p.post_id === postId ? { ...p, likes: res.data.likes } : p
      ));
    } catch (error) {
      console.error('Error liking post:', error);
    }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar esta publicación?')) {
      return;
    }
    
    setDeleting(postId);
    try {
      const token = localStorage.getItem('session_token');
      await axios.delete(`${API}/community/posts/${postId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Publicación eliminada');
      setPosts(posts.filter(p => p.post_id !== postId));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar la publicación');
    } finally {
      setDeleting(null);
    }
  };

  const handleDeleteChallenge = async (challengeId) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar este reto?')) {
      return;
    }
    
    setDeleting(challengeId);
    try {
      const token = localStorage.getItem('session_token');
      await axios.delete(`${API}/community/challenges/${challengeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Reto eliminado');
      setChallenges(challenges.filter(c => c.challenge_id !== challengeId));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar el reto');
    } finally {
      setDeleting(null);
    }
  };

  const handleJoinChallenge = async (challengeId, isAnonymous = false, displayName = '') => {
    try {
      const token = localStorage.getItem('session_token');
      await axios.post(`${API}/community/challenges/${challengeId}/join`, {
        challenge_id: challengeId,
        is_anonymous: isAnonymous,
        display_name: displayName
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('¡Te has unido al reto!');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al unirse al reto');
    }
  };

  const getPostTypeIcon = (type) => {
    const icons = {
      question: <MessageSquare className="w-4 h-4" />,
      tip: <Award className="w-4 h-4" />,
      motivation: <Heart className="w-4 h-4" />,
      achievement: <Trophy className="w-4 h-4" />
    };
    return icons[type] || icons.question;
  };

  const getPostTypeLabel = (type) => {
    const labels = {
      question: 'Pregunta',
      tip: 'Consejo',
      motivation: 'Motivación',
      achievement: 'Logro'
    };
    return labels[type] || 'Publicación';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-purple-50">
      {/* Nav */}
      <nav className="border-b border-slate-200 bg-white/70 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 items-center">
            <Button variant="ghost" onClick={() => navigate('/dashboard')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Volver
            </Button>
            <div className="flex items-center gap-2">
              <OposaiLogo size={36} showText={false} />
              <h1 className="text-xl font-bold">Comunidad TuPlazaIA</h1>
            </div>
            <div className="w-24" />
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Tabs */}
        <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
          {[
            { id: 'feed', label: 'Para ti', icon: <Heart className="w-4 h-4" /> },
            { id: 'posts', label: 'Foro', icon: <MessageSquare className="w-4 h-4" /> },
            { id: 'challenges', label: 'Retos', icon: <Trophy className="w-4 h-4" /> }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full font-medium transition-all ${
                activeTab === tab.id
                  ? 'bg-purple-600 text-white'
                  : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
              }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 mb-6">
          <Button
            onClick={() => setShowNewPostModal(true)}
            className="bg-purple-600 hover:bg-purple-700 text-white rounded-full"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nueva publicación
          </Button>
          {activeTab === 'challenges' && (
            <Button
              onClick={() => setShowNewChallengeModal(true)}
              variant="outline"
              className="rounded-full"
            >
              <Target className="w-4 h-4 mr-2" />
              Crear reto
            </Button>
          )}
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
          </div>
        ) : (
          <>
            {/* Posts */}
            {(activeTab === 'feed' || activeTab === 'posts') && (
              <div className="space-y-4">
                {posts.length === 0 ? (
                  <div className="text-center py-12 bg-white rounded-2xl border border-slate-100">
                    <MessageSquare className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">No hay publicaciones aún</p>
                    <p className="text-sm text-slate-400">¡Sé el primero en publicar!</p>
                  </div>
                ) : (
                  posts.map((post) => (
                    <motion.div
                      key={post.post_id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-white rounded-2xl border border-slate-100 p-5 hover:shadow-md transition-all"
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-400 to-pink-400 flex items-center justify-center text-white font-bold">
                          {post.display_name?.[0] || 'A'}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium text-slate-900">
                              {post.display_name}
                            </span>
                            {post.is_anonymous && (
                              <span className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full">
                                Anónimo
                              </span>
                            )}
                            <span className="text-xs text-slate-400">
                              {new Date(post.created_at).toLocaleDateString('es-ES')}
                            </span>
                          </div>
                          <div className="flex items-center gap-1 text-xs text-purple-600 mb-2">
                            {getPostTypeIcon(post.post_type)}
                            <span>{getPostTypeLabel(post.post_type)}</span>
                          </div>
                          <p className="text-slate-700 whitespace-pre-wrap">{post.content}</p>
                          <div className="flex items-center justify-between mt-3">
                            <div className="flex items-center gap-4">
                              <button
                                onClick={() => handleLikePost(post.post_id)}
                                className="flex items-center gap-1 text-slate-500 hover:text-pink-500 transition-colors"
                              >
                                <Heart className="w-4 h-4" />
                                <span className="text-sm">{post.likes || 0}</span>
                              </button>
                              <button className="flex items-center gap-1 text-slate-500 hover:text-purple-500 transition-colors">
                                <MessageSquare className="w-4 h-4" />
                                <span className="text-sm">{post.replies_count || 0}</span>
                              </button>
                            </div>
                            {post.is_owner && (
                              <button
                                onClick={() => handleDeletePost(post.post_id)}
                                disabled={deleting === post.post_id}
                                className="flex items-center gap-1 text-slate-400 hover:text-red-500 transition-colors"
                                title="Eliminar publicación"
                              >
                                {deleting === post.post_id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Trash2 className="w-4 h-4" />
                                )}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))
                )}
              </div>
            )}

            {/* Challenges */}
            {activeTab === 'challenges' && (
              <div className="space-y-4">
                {challenges.length === 0 ? (
                  <div className="text-center py-12 bg-white rounded-2xl border border-slate-100">
                    <Trophy className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">No hay retos activos</p>
                    <p className="text-sm text-slate-400">¡Crea el primero!</p>
                  </div>
                ) : (
                  challenges.map((challenge) => (
                    <motion.div
                      key={challenge.challenge_id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-white rounded-2xl border border-slate-100 p-5 hover:shadow-md transition-all"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Trophy className="w-5 h-5 text-amber-500" />
                            <h3 className="font-bold text-slate-900">{challenge.title}</h3>
                          </div>
                          <p className="text-slate-600 text-sm mb-3">{challenge.description}</p>
                          <div className="flex items-center gap-4 text-sm text-slate-500">
                            <span className="flex items-center gap-1">
                              <Target className="w-4 h-4" />
                              {challenge.target_questions} preguntas
                            </span>
                            <span className="flex items-center gap-1">
                              <Users className="w-4 h-4" />
                              {challenge.participants_count} participantes
                            </span>
                            <span>
                              Termina: {new Date(challenge.end_date).toLocaleDateString('es-ES')}
                            </span>
                          </div>
                        </div>
                        <div className="ml-4 flex items-center gap-2">
                          {challenge.is_owner && (
                            <button
                              onClick={() => handleDeleteChallenge(challenge.challenge_id)}
                              disabled={deleting === challenge.challenge_id}
                              className="p-2 text-slate-400 hover:text-red-500 transition-colors"
                              title="Eliminar reto"
                            >
                              {deleting === challenge.challenge_id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Trash2 className="w-4 h-4" />
                              )}
                            </button>
                          )}
                          {challenge.is_participating ? (
                            <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                              Participando
                            </span>
                          ) : (
                            <Button
                              size="sm"
                              onClick={() => handleJoinChallenge(challenge.challenge_id)}
                              className="rounded-full bg-purple-600 hover:bg-purple-700"
                            >
                              Unirse
                            </Button>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  ))
                )}
              </div>
            )}

            {/* Support Feed on main tab */}
            {activeTab === 'feed' && challenges.length > 0 && (
              <div className="mt-8">
                <h2 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <Flame className="w-5 h-5 text-orange-500" />
                  Retos activos
                </h2>
                <div className="grid gap-3">
                  {challenges.slice(0, 3).map((challenge) => (
                    <div
                      key={challenge.challenge_id}
                      className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-4 border border-amber-200"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-medium text-slate-900">{challenge.title}</h3>
                          <p className="text-sm text-slate-600">
                            {challenge.participants_count} participantes · {challenge.target_questions} preguntas
                          </p>
                        </div>
                        <Button
                          size="sm"
                          onClick={() => handleJoinChallenge(challenge.challenge_id)}
                          className="rounded-full bg-amber-500 hover:bg-amber-600"
                        >
                          Unirse
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* New Post Modal */}
      <AnimatePresence>
        {showNewPostModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
            onClick={() => setShowNewPostModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl shadow-2xl max-w-lg w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-5 border-b border-slate-100 flex items-center justify-between">
                <h2 className="text-xl font-bold">Nueva publicación</h2>
                <button onClick={() => setShowNewPostModal(false)} className="p-2 hover:bg-slate-100 rounded-full">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <form onSubmit={handleCreatePost} className="p-5 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Tipo de publicación</label>
                  <div className="grid grid-cols-4 gap-2">
                    {[
                      { id: 'question', label: 'Pregunta', icon: <MessageSquare className="w-4 h-4" /> },
                      { id: 'tip', label: 'Consejo', icon: <Award className="w-4 h-4" /> },
                      { id: 'motivation', label: 'Motivación', icon: <Heart className="w-4 h-4" /> },
                      { id: 'achievement', label: 'Logro', icon: <Trophy className="w-4 h-4" /> }
                    ].map(type => (
                      <button
                        key={type.id}
                        type="button"
                        onClick={() => setNewPost({...newPost, post_type: type.id})}
                        className={`p-2 rounded-lg border text-sm flex flex-col items-center gap-1 ${
                          newPost.post_type === type.id
                            ? 'border-purple-500 bg-purple-50 text-purple-700'
                            : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                        }`}
                      >
                        {type.icon}
                        <span className="text-xs">{type.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Contenido</label>
                  <textarea
                    value={newPost.content}
                    onChange={(e) => setNewPost({...newPost, content: e.target.value})}
                    rows={4}
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-purple-500 focus:ring-2 focus:ring-purple-200 outline-none transition-all resize-none"
                    placeholder="Escribe tu publicación..."
                  />
                </div>

                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                  <div className="flex items-center gap-2">
                    {newPost.is_anonymous ? <EyeOff className="w-4 h-4 text-slate-500" /> : <Eye className="w-4 h-4 text-slate-500" />}
                    <span className="text-sm text-slate-600">Publicar como anónimo</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setNewPost({...newPost, is_anonymous: !newPost.is_anonymous})}
                    className={`w-12 h-6 rounded-full transition-colors ${
                      newPost.is_anonymous ? 'bg-purple-600' : 'bg-slate-300'
                    }`}
                  >
                    <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${
                      newPost.is_anonymous ? 'translate-x-6' : 'translate-x-0.5'
                    }`} />
                  </button>
                </div>

                {newPost.is_anonymous && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Nombre ficticio (opcional)
                    </label>
                    <input
                      type="text"
                      value={newPost.display_name}
                      onChange={(e) => setNewPost({...newPost, display_name: e.target.value})}
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-purple-500 focus:ring-2 focus:ring-purple-200 outline-none transition-all"
                      placeholder="Ej: Opositor Valiente"
                    />
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full rounded-full py-6 bg-purple-600 hover:bg-purple-700 text-white font-bold"
                >
                  {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Send className="w-4 h-4 mr-2" /> Publicar</>}
                </Button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* New Challenge Modal */}
      <AnimatePresence>
        {showNewChallengeModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
            onClick={() => setShowNewChallengeModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl shadow-2xl max-w-lg w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-5 border-b border-slate-100 flex items-center justify-between">
                <h2 className="text-xl font-bold">Crear nuevo reto</h2>
                <button onClick={() => setShowNewChallengeModal(false)} className="p-2 hover:bg-slate-100 rounded-full">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <form onSubmit={handleCreateChallenge} className="p-5 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Título del reto *</label>
                  <input
                    type="text"
                    required
                    value={newChallenge.title}
                    onChange={(e) => setNewChallenge({...newChallenge, title: e.target.value})}
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-purple-500 focus:ring-2 focus:ring-purple-200 outline-none transition-all"
                    placeholder="Ej: Reto 1000 preguntas en enero"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Descripción</label>
                  <textarea
                    value={newChallenge.description}
                    onChange={(e) => setNewChallenge({...newChallenge, description: e.target.value})}
                    rows={3}
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-purple-500 focus:ring-2 focus:ring-purple-200 outline-none transition-all resize-none"
                    placeholder="Describe el reto..."
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Objetivo (preguntas)</label>
                    <input
                      type="number"
                      min="10"
                      value={newChallenge.target_questions}
                      onChange={(e) => setNewChallenge({...newChallenge, target_questions: parseInt(e.target.value)})}
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-purple-500 focus:ring-2 focus:ring-purple-200 outline-none transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Fecha límite *</label>
                    <input
                      type="date"
                      required
                      value={newChallenge.end_date}
                      onChange={(e) => setNewChallenge({...newChallenge, end_date: e.target.value})}
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-purple-500 focus:ring-2 focus:ring-purple-200 outline-none transition-all"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-slate-500" />
                    <span className="text-sm text-slate-600">Reto público (cualquiera puede unirse)</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setNewChallenge({...newChallenge, is_public: !newChallenge.is_public})}
                    className={`w-12 h-6 rounded-full transition-colors ${
                      newChallenge.is_public ? 'bg-purple-600' : 'bg-slate-300'
                    }`}
                  >
                    <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${
                      newChallenge.is_public ? 'translate-x-6' : 'translate-x-0.5'
                    }`} />
                  </button>
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full rounded-full py-6 bg-amber-500 hover:bg-amber-600 text-white font-bold"
                >
                  {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Trophy className="w-4 h-4 mr-2" /> Crear reto</>}
                </Button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CommunityPage;
